import {useEffect, useState} from "react";

import {useAuth} from "../AuthContext";
import {clearCache, withCache} from "../cache";
import {useApi} from "../PlayMakersProvider";

interface UseVoteReturn {
  cancelVote: (submissionId?: string) => Promise<void>;
  downVote: (submissionId?: string) => Promise<void>;
  fetched: boolean;
  myVote: number;
  upVote: (submissionId?: string) => Promise<void>;
  voteBalance: number;
}

export const useVote = (submissionId?: string): UseVoteReturn => {
  const {userData} = useAuth();
  const {upVote, downVote, cancelVote, getVote, voteBalance} = useApi();
  const [fetched, setFetched] = useState<boolean>(false); // Boolean tracking if `vote` is up-to-date
  const [myVote, setMyVote] = useState<number>(0); // Current evaluation of "my" vote
  const [balance, setBalance] = useState<number>(0); // Evaluation of voteBalance at load

  // Fetch the vote status of submissionId from the backend
  async function fetchVote() {
    setFetched(false);
    if(!submissionId) return;
    try {
      if(userData) setMyVote(await withCache(getVote, [submissionId], 120_000));
      setBalance(await withCache(voteBalance, [submissionId], 60_000));
      setFetched(true);
    } catch (error) {
      console.error(`Error fetching vote for submission ${submissionId}`, error);
      setMyVote(0);
      setBalance(0);
    }
  }
  useEffect(() => {
    fetchVote();
  }, [submissionId]);

  // When we vote, we will preset the myVote value so that we don't have to wait
  // the several API calls (set, and then get) to get the visual feedback
  // We do not refetch the real balance, since it looks weird for the user
  // if other votes happened in the meantime.
  const doVote = async(id: string | undefined, val: number, f: Function) => {
    const initialVote = myVote;
    const initialBalance = balance;

    try {
      setBalance(prev => prev - myVote + val);
      if(userData) setMyVote(val);
      const actualVal = (await f(id))?.value || 0;
      if(actualVal !== val) {
        setBalance(prev => prev + actualVal - val);
        if(userData) setMyVote(actualVal);
      }

      clearCache("getVote", [id]);
      clearCache("voteBalance", [id]);
    } catch (e) {
      console.error("Error voting", e);
      setBalance(initialBalance);
      if(userData) setMyVote(initialVote);
      throw new Error("Error voting", {cause: e});
    }

  };

  return {
    cancelVote: async(id = submissionId) => doVote(id,  0, cancelVote),
    downVote: async(id = submissionId) => doVote(id, -1, downVote),
    fetched,
    myVote,
    upVote: async(id = submissionId) => doVote(id,  1, upVote),
    voteBalance: balance
  };
};
